import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { mergeContextActions } from 'utils/contextActionsUtils';
import {
  mergeDeviceCharacteristicGroupDetails,
  mergeDeviceDetails
} from 'features/eai/eaiSliceUtils';
import {
  BalanceGroupDetailInterface,
  DeliveryContractDetailInterface,
  DeliveryPointAssignment,
  DeliveryPointDetailInterface,
  DeviceCharacteristicGroupDetailType,
  DeviceDetailInterface,
  EaiStateInterface,
  EmsMarketParticipantDetailInterface,
  EmsMemberAccountDetailInterface,
  EmsPartyDetailInterface,
  GDSInterface,
  IsEaiReadyInterface,
  MarketParticipantForLoggedUserInterface,
  UserDetailInterface
} from './eaiTypes';
import { ServiceLevelSpecificationInterface, UserGroupInterface } from 'models';
import { ResourceContextAction } from 'models/contextActions';
import { EtagInterface } from 'models/etag';
import { PermissionError } from 'utils/constants';
import { CharacteristicValueInterface } from 'models/characteristics';

const initialIsReady: IsEaiReadyInterface = {
  isDeviceListReady: false,
  isDeviceDetailListReady: false,
  isDeviceCharacteristicGroupListForPlanningReady: false,
  isDeviceListForHxDashboardReady: false
};

export const initialState: EaiStateInterface = {
  codebooks: [],
  userGroups: [],
  serviceLevelSpecifications: [],
  contextActions: [],
  characteristicSpecifications: [],
  unitsOfMeasures: [],
  etag: {},
  emsParty: {
    emsPartyList: {
      partyList: []
    }
  },
  emsMemberAccount: {
    emsMemberAccountList: [],
    emsMemberAccountListNotForLogin: [],
    emsMemberAccountDetail: undefined
  },
  marketParticipant: undefined,
  user: {
    userListInterface: {
      userList: []
    },
    userDetail: undefined,
    userRoleNames: undefined
  },
  deliveryPoint: {
    deliveryPointList: {
      deliveryPointList: []
    },
    gdsList: [],
    assignments: [],
    gdsDevicesList: []
  },
  balanceGroup: {
    deliveryPointList: {
      partyList: []
    },
    balanceGroupDetail: undefined,
    balanceGroupsList: { balanceGroupsList: [] }
  },
  device: {
    deviceList: {
      deviceList: [],
      devicesForPlanning: []
    },
    deviceDetail: [],
    deviceCharacteristicGroupDetail: []
  },
  deliveryContract: {
    deliveryContractListInterface: {
      deliveryContractList: []
    }
  },
  permissionError: PermissionError.NO_ERROR,
  showMenu: true,
  isReady: initialIsReady
};

// ==============================|| SLICE ||============================== //

const eaiSlice = createSlice({
  name: 'eai',
  initialState,
  reducers: {
    clearEaiState() {
      return initialState;
    },
    setCodebooks(state, action: PayloadAction<any>) {
      state.codebooks = action.payload;
    },
    setUserGroups(
      state,
      action: PayloadAction<UserGroupInterface[] | undefined>
    ) {
      state.userGroups = action.payload ?? [];
    },
    setServiceLevelSpecifications(
      state,
      action: PayloadAction<ServiceLevelSpecificationInterface[]>
    ) {
      state.serviceLevelSpecifications = action.payload;
    },
    setEaiContextActions(
      state,
      action: PayloadAction<ResourceContextAction[] | undefined>
    ) {
      if (action.payload?.length) {
        state.contextActions = mergeContextActions(
          state.contextActions,
          action.payload
        );
      }
    },
    setCharacteristicSpecifications(state, action: PayloadAction<any>) {
      state.characteristicSpecifications = action.payload || [];
    },
    setUnitsOfMeasures(state, action: PayloadAction<any>) {
      state.unitsOfMeasures = action.payload || [];
    },
    setEaiEtag(state, action: PayloadAction<EtagInterface>) {
      const { entity, etag } = action.payload;
      state.etag = { ...state.etag, [entity]: etag };
    },
    setEmsPartyList(state, action: PayloadAction<any>) {
      const { _embedded, page } = action.payload;

      state.emsParty.emsPartyList.partyList = _embedded?.eMSPartyForListDtoList
        ? _embedded.eMSPartyForListDtoList
        : [];
      state.emsParty.emsPartyList.page = page || undefined;
    },
    setEmsPartyDetail(
      state,
      action: PayloadAction<EmsPartyDetailInterface | undefined>
    ) {
      state.emsParty.emsPartyDetail = action.payload;
    },
    setEmsMemberAccountList(state, action: PayloadAction<any>) {
      state.emsMemberAccount.emsMemberAccountList =
        action.payload.emsMemberAccounts || [];
    },
    setEmsMemberAccountListNotForLogin(state, action: PayloadAction<any>) {
      state.emsMemberAccount.emsMemberAccountListNotForLogin =
        action.payload.emsMemberAccounts || [];
    },
    setEmsMemberAccountDetail(
      state,
      action: PayloadAction<EmsMemberAccountDetailInterface | undefined>
    ) {
      state.emsMemberAccount.emsMemberAccountDetail = action.payload;
    },
    setMarketParticipantDetail(
      state,
      action: PayloadAction<EmsMarketParticipantDetailInterface | undefined>
    ) {
      state.marketParticipant = action.payload;
    },
    setMarketParticipantList(
      state,
      action: PayloadAction<
        MarketParticipantForLoggedUserInterface[] | undefined
      >
    ) {
      state.marketParticipantList = action.payload;
    },
    setUserList(state, action: PayloadAction<any>) {
      const { _embedded, page } = action.payload;

      state.user.userListInterface.userList = _embedded?.userForListDtoList
        ? _embedded.userForListDtoList
        : [];
      state.user.userListInterface.page = page || undefined;
    },
    setUserDetail(
      state,
      action: PayloadAction<UserDetailInterface | undefined>
    ) {
      state.user.userDetail = action.payload;
    },
    setEaiDeliveryPointList(state, action: PayloadAction<any>) {
      const { _embedded, page } = action.payload;

      state.deliveryPoint.deliveryPointList.deliveryPointList =
        _embedded?.deliveryPointListDtoList
          ? _embedded.deliveryPointListDtoList
          : [];
      state.deliveryPoint.deliveryPointList.page = page || undefined;
    },
    setDeliveryPointDetail(
      state,
      action: PayloadAction<DeliveryPointDetailInterface | undefined>
    ) {
      state.deliveryPoint.deliveryPointDetail = action.payload;
    },
    setDeliveryPointCharacteristics(
      state,
      action: PayloadAction<CharacteristicValueInterface[] | undefined>
    ) {
      state.deliveryPoint.deliveryPointCharacteristics = action.payload;
    },
    setDeliveryPointGDSList(state, action: PayloadAction<GDSInterface[]>) {
      state.deliveryPoint.gdsList = action.payload;
    },
    setDeliveryPointGDSDevicesList(state, action: PayloadAction<any>) {
      const { _embedded } = action.payload;

      state.deliveryPoint.gdsDevicesList = _embedded?.deviceDtoList
        ? _embedded.deviceDtoList
        : [];
    },
    setDeliveryPointGDSDetail(
      state,
      action: PayloadAction<GDSInterface | undefined>
    ) {
      state.deliveryPoint.gdsDetail = action.payload;
    },
    setDeliveryPointAssignmentsList(
      state,
      action: PayloadAction<DeliveryPointAssignment[]>
    ) {
      state.deliveryPoint.assignments = action.payload;
    },
    setBalanceGroupsList(state, action: PayloadAction<any>) {
      const { _embedded, page } = action.payload;

      state.balanceGroup.balanceGroupsList.balanceGroupsList =
        _embedded?.balancingGroupDtoList ? _embedded.balancingGroupDtoList : [];
      state.balanceGroup.balanceGroupsList.page = page || undefined;
    },
    setBalanceGroupDetail(
      state,
      action: PayloadAction<BalanceGroupDetailInterface | undefined>
    ) {
      state.balanceGroup.balanceGroupDetail = action.payload;
    },
    setBalanceGroupCharacteristics(
      state,
      action: PayloadAction<CharacteristicValueInterface[] | undefined>
    ) {
      state.balanceGroup.balanceGroupCharacteristics = action.payload;
    },
    setBalanceGroupDeliveryPointList(state, action: PayloadAction<any>) {
      const { deliveryPointBaseInformationDtoList } = action.payload;

      state.balanceGroup.deliveryPointList.partyList =
        deliveryPointBaseInformationDtoList
          ? deliveryPointBaseInformationDtoList
          : [];
    },
    setDeviceList(state, action: PayloadAction<any>) {
      const { _embedded, page } = action.payload;

      state.device.deviceList.deviceList = _embedded?.deviceDtoList
        ? _embedded.deviceDtoList
        : [];
      state.device.deviceList.page = page ? page : undefined;
    },
    setDevicesForPlanning(state, action: PayloadAction<any>) {
      const { _embedded } = action.payload;

      state.device.deviceList.devicesForPlanning = _embedded?.deviceDtoList
        ? _embedded.deviceDtoList
        : [];
    },
    setDeviceDetail(
      state,
      action: PayloadAction<DeviceDetailInterface[] | undefined>
    ) {
      state.device.deviceDetail = action.payload ? action.payload : [];
    },
    mergeDeviceDetail(
      state,
      action: PayloadAction<DeviceDetailInterface[] | undefined>
    ) {
      state.device.deviceDetail = action.payload
        ? mergeDeviceDetails(state.device.deviceDetail, action.payload)
        : [];
    },
    setDeviceCharacteristicGroupDetail(
      state,
      action: PayloadAction<DeviceCharacteristicGroupDetailType[] | undefined>
    ) {
      state.device.deviceCharacteristicGroupDetail = action.payload
        ? action.payload
        : [];
    },
    mergeDeviceCharacteristicGroupDetail(
      state,
      action: PayloadAction<DeviceCharacteristicGroupDetailType[] | undefined>
    ) {
      state.device.deviceCharacteristicGroupDetail = action.payload
        ? mergeDeviceCharacteristicGroupDetails(
            state.device.deviceCharacteristicGroupDetail,
            action.payload
          )
        : [];
    },
    setDeliveryContractList(state, action: PayloadAction<any>) {
      const { _embedded, page } = action.payload;

      state.deliveryContract.deliveryContractListInterface.deliveryContractList =
        _embedded?.deliveryContractDtoList
          ? _embedded.deliveryContractDtoList
          : [];
      state.deliveryContract.deliveryContractListInterface.page =
        page || undefined;
    },
    setDeliveryContractDetail(
      state,
      action: PayloadAction<DeliveryContractDetailInterface | undefined>
    ) {
      state.deliveryContract.deliveryContractDetail = action.payload;
    },
    setDeliveryContractDeliveryPoints(state, action: PayloadAction<any>) {
      state.deliveryContract.deliveryPoints = action.payload;
    },
    setPermissionError(state, action: PayloadAction<PermissionError>) {
      state.permissionError = action.payload;
    },
    setShowGdsDetail(state, action: PayloadAction<boolean | undefined>) {
      state.deliveryPoint.showGdsDetail = action.payload;
    },
    setShowMenu(state, action: PayloadAction<boolean>) {
      state.showMenu = action.payload;
    },
    setIsDeviceListReady(state, action: PayloadAction<boolean>) {
      state.isReady.isDeviceListReady = action.payload;
    },
    setIsDeviceListForHxDashboardReady(state, action: PayloadAction<boolean>) {
      state.isReady.isDeviceListForHxDashboardReady = action.payload;
    },
    setIsDeviceDetailListReady(state, action: PayloadAction<boolean>) {
      state.isReady.isDeviceDetailListReady = action.payload;
    },
    setIsDeviceCharacteristicGroupListForPlanningReady(
      state,
      action: PayloadAction<boolean>
    ) {
      state.isReady.isDeviceCharacteristicGroupListForPlanningReady =
        action.payload;
    },
    clearIsEaiReady(state) {
      state.isReady = initialIsReady;
    }
  }
});

export const {
  clearEaiState,
  setCodebooks,
  setUserGroups,
  setServiceLevelSpecifications,
  setEaiContextActions,
  setCharacteristicSpecifications,
  setUnitsOfMeasures,
  setEaiEtag,
  setEmsPartyList,
  setEmsPartyDetail,
  setEmsMemberAccountList,
  setEmsMemberAccountListNotForLogin,
  setEmsMemberAccountDetail,
  setMarketParticipantDetail,
  setMarketParticipantList,
  setUserList,
  setUserDetail,
  setEaiDeliveryPointList,
  setDeliveryPointDetail,
  setDeliveryPointCharacteristics,
  setDeliveryPointGDSDetail,
  setDeliveryPointGDSList,
  setDeliveryPointGDSDevicesList,
  setDeliveryPointAssignmentsList,
  setBalanceGroupsList,
  setBalanceGroupDetail,
  setBalanceGroupCharacteristics,
  setBalanceGroupDeliveryPointList,
  setDeviceList,
  setDevicesForPlanning,
  setDeviceDetail,
  mergeDeviceDetail,
  setDeviceCharacteristicGroupDetail,
  mergeDeviceCharacteristicGroupDetail,
  setDeliveryContractList,
  setDeliveryContractDetail,
  setDeliveryContractDeliveryPoints,
  setPermissionError,
  setShowGdsDetail,
  setShowMenu,
  setIsDeviceListReady,
  setIsDeviceListForHxDashboardReady,
  setIsDeviceDetailListReady,
  setIsDeviceCharacteristicGroupListForPlanningReady,
  clearIsEaiReady
} = eaiSlice.actions;

export const eai = eaiSlice.reducer;
